import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from "react";
import { LINGUE, type Lingua, type Traduttore, creaTraduttore, linguaSupportata } from "../../i18n/index.ts";
import { ApiError } from "./api.ts";

/**
 * La lingua dell'interfaccia: una preferenza del browser, come il tema.
 *
 * Non quella dell'istanza: due persone sulla stessa installazione possono
 * leggerla in lingue diverse. Il server manda chiavi, la traduzione si fa qui.
 */

const CHIAVE = "lingua";

export function linguaIniziale(salvata: string | null, browser: readonly string[]): Lingua {
  return (
    linguaSupportata(salvata) ??
    browser.map(linguaSupportata).find((lingua) => lingua !== null) ??
    "en"
  );
}

export function leggiLinguaSalvata(): string | null {
  // Una finestra privata può negare l'accesso: vale come nessuna scelta.
  try {
    return localStorage.getItem(CHIAVE);
  } catch {
    return null;
  }
}

type ContestoLingua = Traduttore & { impostaLingua(lingua: Lingua): void };

const Contesto = createContext<ContestoLingua | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lingua, impostaLingua] = useState(() => linguaIniziale(leggiLinguaSalvata(), navigator.languages));

  useEffect(() => {
    document.documentElement.lang = lingua;
    try {
      localStorage.setItem(CHIAVE, lingua);
    } catch {}
  }, [lingua]);

  const valore = useMemo(() => ({ ...creaTraduttore(lingua), impostaLingua }), [lingua]);

  return <Contesto.Provider value={valore}>{children}</Contesto.Provider>;
}

export function useT(): ContestoLingua {
  const valore = useContext(Contesto);
  if (!valore) throw new Error("useT fuori da I18nProvider"); // i18n-ignora
  return valore;
}

export function SelettoreLingua() {
  const { lingua, impostaLingua, t } = useT();

  return (
    <select
      value={lingua}
      onChange={(e) => impostaLingua(e.target.value as Lingua)}
      aria-label={t("comune.lingua")}
      className="rounded-md bg-transparent py-1 text-sm tenue hover:text-[var(--testo)]"
    >
      {LINGUE.map((l) => (
        <option key={l.codice} value={l.codice}>
          {l.nome}
        </option>
      ))}
    </select>
  );
}

/** Il testo di un errore nella lingua dell'interfaccia, o quello del server se la chiave è nuova. */
export function testoErrore(errore: unknown, t: Traduttore): string {
  if (errore instanceof ApiError && errore.key) {
    return t.traduci({ chiave: errore.key, parametri: errore.params }) ?? errore.message;
  }
  return errore instanceof Error ? errore.message : String(errore);
}
